export interface Email {
  messageId: string;
  threadId: string;
  from: string;
  fromName: string;
  to: string[];
  subject: string;
  snippet: string;
  body: string;
  timestamp: string;
  readStatus: boolean;
  labels: string[];
  attachments: Attachment[];
  ticketId?: string;
}

export interface Attachment {
  id: string;
  filename: string;
  size: string;
  type: string;
}

export interface Tenant {
  tenantId: string;
  displayName: string;
  domain: string;
  role: 'admin' | 'member' | 'readonly';
}

export interface Rule {
  ruleId: string;
  name: string;
  conditionType: 'sender_domain' | 'sender_email' | 'subject_contains' | 'recipient';
  conditionValue: string;
  action: 'forward' | 'drop';
  destination?: string;
  active: boolean;
}

export interface Ticket {
  ticketId: string;
  threadId: string;
  title: string;
  status: 'Open' | 'In Progress' | 'Resolved' | 'Closed';
  assignee: string;
  lastUpdated: string;
  timeline: { event: string; timestamp: string }[];
}

export const mockTenants: Tenant[] = [
  { tenantId: 'freight-ops', displayName: 'Freight Operations', domain: 'freight-ops@amazon.com', role: 'admin' },
  { tenantId: 'sales-outreach', displayName: 'Sales Outreach', domain: 'sales-outreach@amazon.com', role: 'member' },
];

export const mockEmails: Email[] = [
  {
    messageId: 'msg-001',
    threadId: 'thread-001',
    from: 'john.smith@dhl.com',
    fromName: 'John Smith',
    to: ['freight-ops@amazon.com'],
    subject: 'RE: Lane SEA-LAX Rate Quote — Capacity Confirmed',
    snippet:
      'Hi team, we can confirm capacity for the SEA-LAX lane at the quoted rate of $2,450 per load. Our fleet has availability starting...',
    body: `<p>Hi team,</p>
<p>We can confirm capacity for the SEA-LAX lane at the quoted rate of <strong>$2,450 per load</strong>. Our fleet has availability starting Monday, August 18th.</p>
<p>Key details:</p>
<ul>
<li>Lane: Seattle (SEA) → Los Angeles (LAX)</li>
<li>Rate: $2,450/load</li>
<li>Capacity: 3 trucks/day</li>
<li>Start date: August 18, 2026</li>
<li>Contract term: 90 days</li>
</ul>
<p>Please confirm if you'd like to proceed and I'll send the contract paperwork.</p>
<p>Best regards,<br/>John Smith<br/>Director of Operations, DHL Freight</p>`,
    timestamp: '2026-08-13T14:32:00Z',
    readStatus: false,
    labels: ['Tier-1 Carrier', 'Rate Quote'],
    attachments: [{ id: 'att-001', filename: 'DHL_Rate_Quote_SEA_LAX.pdf', size: '245 KB', type: 'pdf' }],
    ticketId: 'TT-4821',
  },
  {
    messageId: 'msg-002',
    threadId: 'thread-002',
    from: 'maria.garcia@fedex.com',
    fromName: 'Maria Garcia',
    to: ['freight-ops@amazon.com'],
    subject: 'Unable to provide capacity — SEA-LAX lane',
    snippet:
      'Unfortunately we are unable to commit to the SEA-LAX lane at this time due to existing contractual obligations in the Pacific Northwest...',
    body: `<p>Hi Freight Operations team,</p>
<p>Unfortunately we are unable to commit to the SEA-LAX lane at this time due to existing contractual obligations in the Pacific Northwest region.</p>
<p>We may have capacity opening up in Q4 2026 — happy to revisit at that point if the need persists.</p>
<p>Best,<br/>Maria Garcia<br/>Account Manager, FedEx Freight</p>`,
    timestamp: '2026-08-13T11:15:00Z',
    readStatus: true,
    labels: ['Tier-1 Carrier'],
    attachments: [],
  },
  {
    messageId: 'msg-003',
    threadId: 'thread-003',
    from: 'ops-notify@xpologistics.com',
    fromName: 'XPO Logistics',
    to: ['freight-ops@amazon.com'],
    subject: 'Automated: Delivery confirmation — PO#AF-29841',
    snippet:
      'This is an automated notification. Shipment PO#AF-29841 has been delivered to destination warehouse SEA-3...',
    body: `<p><strong>Delivery Confirmation</strong></p>
<p>Shipment <code>PO#AF-29841</code> has been delivered.</p>
<table style="border-collapse:collapse; margin:16px 0;">
<tr><td style="padding:4px 12px; border:1px solid #ddd;"><strong>PO Number</strong></td><td style="padding:4px 12px; border:1px solid #ddd;">AF-29841</td></tr>
<tr><td style="padding:4px 12px; border:1px solid #ddd;"><strong>Delivered to</strong></td><td style="padding:4px 12px; border:1px solid #ddd;">SEA-3 Warehouse</td></tr>
<tr><td style="padding:4px 12px; border:1px solid #ddd;"><strong>Time</strong></td><td style="padding:4px 12px; border:1px solid #ddd;">Aug 13, 2026 at 09:42 AM PDT</td></tr>
<tr><td style="padding:4px 12px; border:1px solid #ddd;"><strong>Signed by</strong></td><td style="padding:4px 12px; border:1px solid #ddd;">R. Martinez</td></tr>
</table>
<p>— XPO Logistics Automated System</p>`,
    timestamp: '2026-08-13T09:42:00Z',
    readStatus: true,
    labels: ['Automated'],
    attachments: [
      { id: 'att-002', filename: 'POD_AF29841.pdf', size: '128 KB', type: 'pdf' },
      { id: 'att-003', filename: 'delivery_photo.jpg', size: '1.2 MB', type: 'image' },
    ],
    ticketId: 'TT-4815',
  },
  {
    messageId: 'msg-004',
    threadId: 'thread-004',
    from: 'contracts@schneider.com',
    fromName: 'Schneider National',
    to: ['freight-ops@amazon.com'],
    subject: 'Contract Renewal — 2026 Q4 Term Sheet',
    snippet:
      'Please find attached the updated term sheet for our Q4 2026 contract renewal. Key changes include adjusted fuel surcharge rates...',
    body: `<p>Dear Amazon Freight Operations,</p>
<p>Please find attached the updated term sheet for our Q4 2026 contract renewal.</p>
<p><strong>Key changes from current contract:</strong></p>
<ul>
<li>Fuel surcharge: Updated to DOE index + 12% (was +10%)</li>
<li>Volume commitment: 150 loads/week minimum (unchanged)</li>
<li>New lanes added: PHX-DEN, DEN-ORD</li>
<li>Rate increase: 3.2% across existing lanes</li>
</ul>
<p>Please review and let us know if you have questions. We'd like to finalize by August 25th.</p>
<p>Regards,<br/>Schneider National Contracts Team</p>`,
    timestamp: '2026-08-12T16:20:00Z',
    readStatus: false,
    labels: ['Contract'],
    attachments: [{ id: 'att-004', filename: 'Schneider_Q4_2026_TermSheet.docx', size: '89 KB', type: 'document' }],
  },
  {
    messageId: 'msg-005',
    threadId: 'thread-005',
    from: 'sarah.chen@amazon.com',
    fromName: 'Sarah Chen',
    to: ['freight-ops@amazon.com'],
    subject: 'Internal: Weekly carrier performance review — action items',
    snippet:
      "Following up on today's carrier performance review. Three carriers flagged for SLA breaches this week: Werner (2 late deliveries)...",
    body: `<p>Team,</p>
<p>Following up on today's carrier performance review. Three carriers flagged for SLA breaches this week:</p>
<ol>
<li><strong>Werner Enterprises</strong> — 2 late deliveries (SEA-SFO lane), root cause: driver shortage</li>
<li><strong>Knight-Swift</strong> — 1 damaged shipment (LAX-PHX), claim filed</li>
<li><strong>Heartland Express</strong> — 3 missed pickup windows, escalation sent to account manager</li>
</ol>
<p>Action items:</p>
<ul>
<li>@freight-ops: Review Werner capacity plan for next week</li>
<li>@freight-ops: Follow up on Knight-Swift damage claim status</li>
<li>@freight-ops: Schedule call with Heartland Express AM by Friday</li>
</ul>
<p>—Sarah</p>`,
    timestamp: '2026-08-12T10:05:00Z',
    readStatus: true,
    labels: ['Internal', 'Action Required'],
    attachments: [],
    ticketId: 'TT-4810',
  },
  {
    messageId: 'msg-006',
    threadId: 'thread-001',
    from: 'freight-ops@amazon.com',
    fromName: 'Freight Operations',
    to: ['john.smith@dhl.com'],
    subject: 'RE: Lane SEA-LAX Rate Quote — Request for Capacity',
    snippet:
      'Hi John, we are looking for capacity on the SEA-LAX lane starting next week. Can you provide a rate quote for 3 trucks/day...',
    body: `<p>Hi John,</p>
<p>We are looking for capacity on the SEA-LAX lane starting next week. Can you provide a rate quote for 3 trucks/day, 5 days/week?</p>
<p>Requirements:</p>
<ul>
<li>53' dry van</li>
<li>Max transit: 24 hours</li>
<li>Drop trailer capable</li>
</ul>
<p>Please respond by EOD Friday if possible.</p>
<p>Thanks,<br/>Amazon Freight Operations</p>`,
    timestamp: '2026-08-11T09:00:00Z',
    readStatus: true,
    labels: [],
    attachments: [],
  },
];

export const mockRules: Rule[] = [
  {
    ruleId: 'rule-001',
    name: 'Forward DHL emails to procurement',
    conditionType: 'sender_domain',
    conditionValue: '@dhl.com',
    action: 'forward',
    destination: 'procurement-lead@amazon.com',
    active: true,
  },
  {
    ruleId: 'rule-002',
    name: 'Drop marketing emails',
    conditionType: 'subject_contains',
    conditionValue: 'unsubscribe',
    action: 'drop',
    active: true,
  },
  {
    ruleId: 'rule-003',
    name: 'Forward contract emails to legal',
    conditionType: 'subject_contains',
    conditionValue: 'contract',
    action: 'forward',
    destination: 'freight-legal@amazon.com',
    active: false,
  },
];

export const mockTickets: Ticket[] = [
  {
    ticketId: 'TT-4821',
    threadId: 'thread-001',
    title: 'SEA-LAX Lane Rate Quote — DHL',
    status: 'In Progress',
    assignee: 'vjjoseph',
    lastUpdated: '2026-08-13T14:32:00Z',
    timeline: [
      { event: 'New email received from john.smith@dhl.com', timestamp: '2026-08-13T14:32:00Z' },
      { event: 'Status changed to In Progress', timestamp: '2026-08-12T10:00:00Z' },
      { event: 'Ticket created from email thread', timestamp: '2026-08-11T09:05:00Z' },
    ],
  },
  {
    ticketId: 'TT-4815',
    threadId: 'thread-003',
    title: 'Delivery PO#AF-29841 — XPO',
    status: 'Resolved',
    assignee: 'chohasin',
    lastUpdated: '2026-08-13T09:42:00Z',
    timeline: [
      { event: 'Delivery confirmed — auto-resolved', timestamp: '2026-08-13T09:42:00Z' },
      { event: 'Ticket created from shipment notification', timestamp: '2026-08-10T14:00:00Z' },
    ],
  },
  {
    ticketId: 'TT-4810',
    threadId: 'thread-005',
    title: 'Weekly Carrier Performance — SLA Breaches',
    status: 'Open',
    assignee: 'gakhar',
    lastUpdated: '2026-08-12T10:05:00Z',
    timeline: [{ event: 'Ticket created from internal review email', timestamp: '2026-08-12T10:05:00Z' }],
  },
];
